import { useState } from "react";

const wishTags = [
  "微醺品酒會", "手作體驗工坊", "主題攝影外拍", "星空冥想/瑜珈",
  "城市尋寶解謎", "電競/桌遊對抗", "運動交友日", "人類圖交流會",
  "潛水體驗", "登山健行", "烹飪課程", "寵物友善活動",
];

export default function WishPool() {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wish, setWish] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleWishChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 500) {
      setWish(e.target.value);
      setCharCount(e.target.value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    setSubmitting(true);
    const formData = new URLSearchParams();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("wish", wish);
    formData.append("tags", selectedTags.join(", "));

    try {
      await fetch("https://readdy.ai/api/form/d7bv5d8r4h7u93gnpvq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="wish-pool" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Info */}
          <div>
            <p className="text-red-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Wish Pool
            </p>
            <h2
              className="text-3xl md:text-5xl font-black text-gray-900 mb-6"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              許願池
              <br />
              <span className="text-red-600">由你決定！</span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              想辦什麼活動，由你決定！
              <br />
              留下你最想參加的主題，讓紅歡密為你量身打造。
              <br />
              你的每一個願望，都是我們下一場活動的靈感。
            </p>

            {/* Popular wishes */}
            <div className="bg-stone-50 rounded-2xl p-6">
              <p className="text-gray-700 font-semibold text-sm mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-red-500" />
                熱門許願主題
              </p>
              <div className="flex flex-wrap gap-2">
                {wishTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedTags.includes(tag)
                        ? "bg-red-600 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-stone-50 rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                  <i className="ri-heart-fill text-3xl text-red-600" />
                </div>
                <h3
                  className="text-2xl font-bold text-gray-900 mb-2"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  許願成功！
                </h3>
                <p className="text-gray-500 text-base">
                  謝謝你的許願，紅歡密團隊已收到你的心願。
                  <br />
                  我們會努力把它變成現實！
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="wish-pool-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <h3
                  className="text-xl font-bold text-gray-900 mb-1"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  投下你的心願
                </h3>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    你的名字 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="請輸入你的名字"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="請輸入你的 Email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>

                {selectedTags.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700">已選主題</label>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTags.map((tag) => (
                        <span
                          key={tag}
                          className="bg-red-100 text-red-700 text-xs px-2.5 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <input type="hidden" name="tags" value={selectedTags.join(", ")} />
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    你的心願（選填）
                  </label>
                  <textarea
                    name="wish"
                    value={wish}
                    onChange={handleWishChange}
                    placeholder="告訴我們你最想參加什麼樣的活動..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors resize-none"
                  />
                  <p className={`text-xs text-right ${charCount >= 500 ? "text-red-500" : "text-gray-400"}`}>
                    {charCount}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !name.trim() || !email.trim()}
                  className="w-full py-3.5 rounded-full bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      送出中...
                    </>
                  ) : (
                    <>
                      <i className="ri-star-line" />
                      投下我的心願
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
